import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAccounts } from "../lib/account";
import { Trophy, Warning, Clock, Fire, TrendUp, TrendDown } from "@phosphor-icons/react";

function Delta({ value, invert = false }) {
  if (value === 0 || value === undefined) return <span className="text-[10px] text-muted-foreground">—</span>;
  const positive = invert ? value < 0 : value > 0;
  const Icon = positive ? TrendUp : TrendDown;
  const cls = positive ? "text-emerald-400" : "text-red-400";
  return (
    <span className={`text-[10px] font-mono-num inline-flex items-center gap-0.5 ${cls}`}>
      <Icon size={10} weight="bold" />{value > 0 ? "+" : ""}{value}
    </span>
  );
}

function Metric({ icon: Icon, label, value, sub, tone = "default", testid, delta }) {
  const tones = { warn: "text-amber-400", good: "text-emerald-400", bad: "text-red-400", default: "text-foreground" };
  return (
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-md bg-secondary ${tones[tone]}`}><Icon size={18} weight="duotone" /></div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</p>
          {delta}
        </div>
        <p data-testid={testid} className={`font-mono-num text-lg sm:text-2xl font-bold ${tones[tone]} truncate`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

export default function DisciplineCard({ refreshKey = 0 }) {
  const { activeId } = useAccounts();
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    if (!activeId) return;
    api.get(`/analytics/discipline?account_id=${activeId}&compare=${period}`)
      .then(({ data }) => setD(data)).catch(() => {});
  }, [activeId, refreshKey, period]);

  if (!d) return null;
  const curStreakTone = d.current_streak_type === "win" ? "good" : d.current_streak_type === "loss" ? "bad" : "default";
  const deltas = d.compare?.deltas || {};

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
      <div className="flex items-start justify-between mb-4 sm:mb-5 gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Behavior</p>
          <h3 className="font-display text-base sm:text-lg font-semibold mt-1">Discipline Score</h3>
        </div>
        {d.compare && (
          <select value={period} onChange={e => setPeriod(e.target.value)}
            className="bg-secondary border border-border rounded-md px-2 py-1 text-[10px] tracking-[0.15em] uppercase text-muted-foreground"
            data-testid="disc-period">
            <option value="week">vs prev 7d</option>
            <option value="month">vs prev 30d</option>
            <option value="quarter">vs prev 90d</option>
          </select>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-5">
        <Metric icon={Trophy} label="Max win streak" value={d.max_win_streak} sub="consecutive wins" tone="good"
          testid="disc-max-win" delta={<Delta value={deltas.max_win_streak} />} />
        <Metric icon={Fire} label="Max loss streak" value={d.max_loss_streak} sub="consecutive losses" tone="bad"
          testid="disc-max-loss" delta={<Delta value={deltas.max_loss_streak} invert />} />
        <Metric icon={Fire}
          label={`Current ${d.current_streak_type !== "none" ? d.current_streak_type : "streak"}`}
          value={d.current_streak} sub="live" tone={curStreakTone} testid="disc-current-streak" />
        <Metric icon={Clock} label="Avg hold time"
          value={d.avg_hold_hours < 1 ? `${Math.round(d.avg_hold_hours * 60)}m` : `${d.avg_hold_hours}h`}
          sub="entry → exit" testid="disc-avg-hold"
          delta={<Delta value={deltas.avg_hold_hours} />} />
      </div>

      <div className="mt-6 pt-5 border-t border-border">
        <div className="flex items-center gap-2 mb-2">
          <Warning size={14} className="text-amber-400" />
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Over-trading days</p>
          {deltas.over_trading_days !== undefined && (
            <Delta value={deltas.over_trading_days} invert />
          )}
        </div>
        {d.over_trading_days?.length === 0 ? (
          <p className="text-sm text-muted-foreground">Clean discipline — no over-trading days flagged.</p>
        ) : (
          <div className="space-y-1">
            {d.over_trading_days.map(o => (
              <div key={o.date} className="flex justify-between text-xs" data-testid={`over-day-${o.date}`}>
                <span className="font-mono-num text-muted-foreground">{o.date}</span>
                <span className="font-mono-num text-amber-400">{o.trades} trades</span>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground pt-2">
              Avg across active days: {d.avg_trades_per_active_day}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
